import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, ChevronLeft, ChevronRight, Maximize2, Layers, QuoteIcon } from 'lucide-react';
import PortalModal from './PortalModal';
import ENV from '../config/env';
import { useOverlayTool } from '../context/OverlayToolContext';

// Helper: Check if string is a color hex
const isColorHex = (str) => {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(str);
};

const getFullImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('blob:')) return imagePath;
    if (imagePath.startsWith('http')) return imagePath;
    // Add the file server URL prefix
    return `${ENV.FILE_SERVER_URL}${imagePath}`;
};

// Helper to convert all images in an array to full URLs
const getFullImageUrls = (images) => {
    if (!images || !Array.isArray(images)) return [];
    return images.map(img => getFullImageUrl(img));
};

// Helper: Normalize color value
const normalizeColor = (color) => {
    if (!color) return '#cccccc';
    if (isColorHex(color)) return color;
    const colorMap = {
        'AZUL': '#0000FF',
        'ROJO': '#FF0000',
        'VERDE': '#00FF00',
        'AMARILLO': '#FFFF00',
        'NEGRO': '#000000',
        'BLANCO': '#FFFFFF',
        'GRIS': '#808080',
        'MORADO': '#800080',
        'NARANJA': '#FFA500',
        'ROSA': '#FFC0CB',
        'CAFE': '#8B4513',
        'CAFÉ': '#8B4513',
        'MARRON': '#8B4513',
    };
    const upperColor = color.toUpperCase();
    return colorMap[upperColor] || '#cccccc';
};

// Component: Color display with dots
const ColorDisplay = ({ colors }) => {
    if (!colors) return null;

    let colorArray = [];
    if (Array.isArray(colors)) {
        colorArray = colors;
    } else if (typeof colors === 'string') {
        colorArray = colors.split(',').map(c => c.trim());
    } else {
        return <span className="text-sm text-gray-700">{String(colors)}</span>;
    }

    colorArray = colorArray.filter(c => c && c !== '');

    if (colorArray.length === 0) return null;

    return (
        <div className="flex flex-wrap items-center gap-2">
            {colorArray.map((color, idx) => {
                const normalizedColor = normalizeColor(color);
                return (
                    <div
                        key={idx}
                        className="w-6 h-6 rounded-full border border-gray-300 shadow-sm"
                        style={{ backgroundColor: normalizedColor }}
                        title={color}
                    />
                );
            })}
        </div>
    );
};

// Component: Full-size image modal (unchanged)
const FullSizeImageModal = ({ images, currentIndex, isOpen, onClose, onNext, onPrev, onThumbnailClick }) => {
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isOpen) return;
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowRight') onNext();
            if (e.key === 'ArrowLeft') onPrev();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose, onNext, onPrev]);

    if (!isOpen || !images || images.length === 0) return null;

    // Helper to get full image URL (in case raw paths are passed)
    const getFullImageUrl = (imagePath) => {
        if (!imagePath) return '';
        if (imagePath.startsWith('blob:')) return imagePath;
        if (imagePath.startsWith('http')) return imagePath;
        return `${ENV.FILE_SERVER_URL}${imagePath}`;
    };

    return (
        <PortalModal isOpen={isOpen}>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-xl z-[20002] flex items-center justify-center"
                onClick={onClose}
            >
                <button
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0 }}
                    onClick={(e) => { e.stopPropagation(); onClose(); }}
                    className="absolute top-4 right-4 z-[10000] bg-secondary/50 hover:bg-primary text-gray-700 hover:text-white p-3 rounded-full shadow-2xl transition-all"
                >
                    <X size={24} />
                </button>

                {images.length > 1 && (
                    <>
                        <button
                            onClick={(e) => { e.stopPropagation(); onPrev(); }}
                            className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 border border-white/20 p-3 rounded-full text-white hover:bg-primary transition-colors"
                        >
                            <ChevronLeft size={24} />
                        </button>
                        <button
                            onClick={(e) => { e.stopPropagation(); onNext(); }}
                            className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 border border-white/20 p-3 rounded-full text-white hover:bg-primary transition-colors"
                        >
                            <ChevronRight size={24} />
                        </button>
                    </>
                )}

                <div className="absolute top-4 left-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                    {currentIndex + 1} / {images.length}
                </div>

                <motion.div
                    key={currentIndex}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="w-full h-full flex items-center justify-center p-4 md:p-8"
                    onClick={(e) => e.stopPropagation()}
                >
                    <img
                        src={getFullImageUrl(images[currentIndex])}
                        alt={`Product image ${currentIndex + 1}`}
                        className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg"
                        style={{ maxHeight: 'calc(100vh - 120px)' }}
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `${ENV.FILE_SERVER_URL}/images/default-product.jpg`;
                        }}
                    />
                </motion.div>

                {images.length > 1 && (
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 overflow-x-auto max-w-[90vw] p-2 bg-black/50 rounded-lg">
                        {images.map((img, idx) => (
                            <button
                                key={idx}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onThumbnailClick(idx);
                                }}
                                className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${idx === currentIndex ? 'border-primary scale-110' : 'border-transparent opacity-60 hover:opacity-100'
                                    }`}
                            >
                                <img
                                    src={getFullImageUrl(img)}
                                    alt={`Thumbnail ${idx + 1}`}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = `${ENV.FILE_SERVER_URL}/images/default-product.jpg`;
                                    }}
                                />
                            </button>
                        ))}
                    </div>
                )}
            </motion.div>
        </PortalModal>
    );
};

// Component: Image gallery with navigation
const ImageGallery = ({ images, productName, onImageClick, onActiveImageChange }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const hasMultipleImages = images.length > 1;

    // Reset index when images array changes (important for switching between variants)
    useEffect(() => {
        setCurrentIndex(0);
    }, [images]);

    // Report the active image URL whenever it changes
    useEffect(() => {
        if (onActiveImageChange && images[currentIndex]) {
            onActiveImageChange(images[currentIndex], currentIndex);
        }
    }, [currentIndex, images, onActiveImageChange]);

    const nextImage = useCallback((e) => {
        e?.stopPropagation();
        setCurrentIndex((prev) => (prev + 1) % images.length);
    }, [images.length]);

    const prevImage = useCallback((e) => {
        e?.stopPropagation();
        setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    }, [images.length]);

    if (!images.length) {
        return (
            <div className="h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                <p className="text-gray-400">Sin imagen disponible</p>
            </div>
        );
    }

    // Force re-render when images change by using the first image as part of the key
    return (
        <div key={`gallery-${images[0]}-${images.length}`} className="space-y-3">
            {/* Main image */}
            <div className="relative bg-gray-100 rounded-xl overflow-hidden" style={{ minHeight: '300px' }}>
                <img
                    key={`main-img-${images[currentIndex]}`}
                    src={images[currentIndex]}
                    alt={productName}
                    className="w-full h-full object-contain p-4"
                    style={{ maxHeight: '400px', margin: '0 auto' }}
                    onError={(e) => {
                        console.error('Image failed to load:', images[currentIndex]);
                        e.target.onerror = null;
                        e.target.src = `${ENV.FILE_SERVER_URL}/images/default-product.jpg`;
                    }}
                    onLoad={() => console.log('Image loaded successfully:', images[currentIndex])}
                />

                {/* Fullscreen button */}
                <button
                    onClick={() => onImageClick(images, currentIndex)}
                    className="absolute bottom-4 right-4 bg-white/90 hover:bg-primary hover:text-white p-3 rounded-full shadow-lg transition-colors"
                    title="Ver imagen completa"
                >
                    <Maximize2 size={20} />
                </button>

                {/* Image navigation */}
                {hasMultipleImages && (
                    <>
                        <button
                            onClick={prevImage}
                            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-primary hover:text-white p-2 rounded-full shadow-lg transition-colors"
                        >
                            <ChevronLeft size={20} />
                        </button>
                        <button
                            onClick={nextImage}
                            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-primary hover:text-white p-2 rounded-full shadow-lg transition-colors"
                        >
                            <ChevronRight size={20} />
                        </button>
                        <div className="absolute bottom-4 left-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                            {currentIndex + 1} / {images.length}
                        </div>
                    </>
                )}
            </div>

            {/* Thumbnail strip */}
            {hasMultipleImages && (
                <div className="flex flex-wrap gap-2 overflow-x-auto pb-2">
                    {images.map((img, idx) => (
                        <button
                            key={`thumb-${idx}-${img}`}
                            onClick={() => setCurrentIndex(idx)}
                            className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${idx === currentIndex ? 'border-primary scale-105' : 'border-transparent opacity-70 hover:opacity-100'
                                }`}
                        >
                            <img
                                src={img}
                                alt={`Thumbnail ${idx + 1}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = `${ENV.FILE_SERVER_URL}/images/default-product.jpg`;
                                }}
                            />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// Component: Color variant selector for main products with Vista general option
const ColorVariantSelector = ({ variants, selectedVariant, onVariantSelect, productImages }) => {
    if (!variants || variants.length === 0) return null;

    // Create a "Vista general" option to show main product images
    const generalOption = {
        sku: 'general-default',
        color: 'Vista general',
        images: getFullImageUrls(productImages || []),
        isGeneralOption: true
    };

    const allOptions = [generalOption, ...variants.map(variant => ({
        ...variant,
        images: getFullImageUrls(variant.images || [])
    }))];

    return (
        <div className="mb-6 border-t border-gray-200 pt-4">
            <h3 className="text-sm font-medium text-gray-500 mb-3">Colores disponibles</h3>
            <div className="flex flex-wrap gap-3">
                {allOptions.map((option) => {
                    const isSelected = option.isGeneralOption
                        ? selectedVariant?.isGeneralOption
                        : selectedVariant?.sku === option.sku;

                    const hasColor = option.color && !option.isGeneralOption && isColorHex(option.color);

                    return (
                        <button
                            key={option.sku || 'general-default'}
                            onClick={() => onVariantSelect(option)}
                            className={`group relative flex flex-col items-center p-3 rounded-xl border-2 transition-all min-w-[100px] ${isSelected
                                ? 'border-primary bg-primary/5'
                                : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50'
                                }`}
                        >
                            {hasColor && (
                                <div
                                    className="w-8 h-8 rounded-full mb-2 border border-gray-300 shadow-sm"
                                    style={{ backgroundColor: normalizeColor(option.color) }}
                                />
                            )}
                            <span className="text-sm font-medium text-gray-900 text-center">
                                {option.isGeneralOption ? 'Vista general' : (option.color || 'Color')}
                            </span>
                            {!option.isGeneralOption && option.images?.length > 0 && (
                                <span className="text-xs text-gray-400 mt-1">
                                    {option.images.length} imágenes
                                </span>
                            )}
                            {option.isGeneralOption && productImages?.length > 0 && (
                                <span className="text-xs text-gray-400 mt-1">
                                    {productImages.length} imágenes
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

// Component: Variant selector for partner products
const PartnerVariantSelector = ({
    variants,
    selectedVariant,
    onVariantSelect,
    productImages
}) => {
    if (!variants || variants.length === 0) return null;

    const productOption = {
        sku: 'product-default',
        color: 'Producto',
        images: getFullImageUrls(productImages || []),
        isProductOption: true
    };

    const allOptions = [productOption, ...variants.map(variant => ({
        ...variant,
        images: getFullImageUrls(variant.images || [])
    }))];

    return (
        <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-500 mb-3">Opciones de visualización</h3>
            <div className="flex flex-wrap gap-3">
                {allOptions.map((option) => {
                    const isSelected = option.isProductOption
                        ? selectedVariant?.isProductOption
                        : selectedVariant?.sku === option.sku;

                    const hasColor = option.color && !option.isProductOption && isColorHex(option.color);

                    return (
                        <button
                            key={option.sku || 'product-default'}
                            onClick={() => onVariantSelect(option)}
                            className={`group relative flex flex-col items-center p-3 rounded-xl border-2 transition-all min-w-[100px] ${isSelected
                                ? 'border-primary bg-primary/5'
                                : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50'
                                }`}
                        >
                            {hasColor && (
                                <div
                                    className="w-8 h-8 rounded-full mb-2 border border-gray-300 shadow-sm"
                                    style={{ backgroundColor: normalizeColor(option.color) }}
                                />
                            )}
                            <span className="text-sm font-medium text-gray-900 text-center">
                                {option.isProductOption ? 'Vista general' : (option.color || 'Color único')}
                            </span>
                            {option.images?.length > 0 && (
                                <span className="text-xs text-gray-400 mt-1">
                                    {option.images.length} {option.images.length === 1 ? 'imagen' : 'imágenes'}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

// Main ProductDetailModal Component
const ProductDetailModal = ({ product, isOpen, onClose, onQuote, isPartnerProduct = false }) => {
    const scrollPositionRef = useRef(0);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [fullSizeImage, setFullSizeImage] = useState({
        isOpen: false,
        images: [],
        currentIndex: 0
    });

    // Track which image is currently visible in the gallery
    const [activeImageUrl, setActiveImageUrl] = useState('');
    const [activeImageIndex, setActiveImageIndex] = useState(0);

    const { sendToOverlayTool } = useOverlayTool();

    // Callback passed to ImageGallery to receive the active image
    const handleActiveImageChange = useCallback((url, index) => {
        setActiveImageUrl(url);
        setActiveImageIndex(index);
    }, []);

    // Build the OverlayTool-compatible product and send it
    const handleSendToOverlayTool = useCallback(() => {
        if (!product) return;

        let overlayProduct;

        if (isPartnerProduct) {
            // Partner product: proxy images through the API to handle CORS
            const proxyImage = (url) =>
                `${ENV.API_BASE}/partner-products/image-proxy?url=${encodeURIComponent(url)}`;

            // Use allImages (product + variant images) so nav controls have all images
            const allImages = (product.allImages || product.productImages || []).map(proxyImage);

            overlayProduct = {
                id: `partner-${product.id}`,
                originalId: product.id,
                name: product.name,
                category: product.category || 'General',
                subCategory: product.subCategory || '',
                brand: product.brand || '',
                images: allImages,
                source: 'partner',
                sourceLabel: 'Promocionales',
                originalProduct: product
            };
        } else {
            // Main product: prefix with file server URL
            const toFull = (img) =>
                img.startsWith('http') ? img : `${ENV.FILE_SERVER_URL}${img}`;

            const mainImages = (product.images || []).map(toFull);
            const variantImages = (product.variants || []).flatMap(v =>
                (v.images || []).map(toFull)
            );
            const allImages = [...new Set([...mainImages, ...variantImages])];

            overlayProduct = {
                id: `main-${product.id}`,
                originalId: product.id,
                name: product.name,
                category: product.category || 'General',
                subCategory: product.subCategory || '',
                brand: product.brand || '',
                images: allImages,
                originalProduct: product,
                variants: product.variants || [],
                source: 'main',
                sourceLabel: 'Print Studio'
            };
        }

        // Resolve the proxied version of the active image URL for the canvas
        let resolvedImageUrl = activeImageUrl;
        if (isPartnerProduct && activeImageUrl && !activeImageUrl.includes('image-proxy')) {
            resolvedImageUrl = `${ENV.API_BASE}/partner-products/image-proxy?url=${encodeURIComponent(activeImageUrl)}`;
        }

        sendToOverlayTool({
            overlayProduct,
            imageUrl: resolvedImageUrl || overlayProduct.images[0],
            imageIndex: activeImageIndex,
            source: isPartnerProduct ? 'partner' : 'main',
            category: product.category || ''
        });

        // Close the modal first so body scroll-lock is released, then scroll
        onClose();
        setTimeout(() => {
            const section = document.getElementById('logotool');
            if (section) section.scrollIntoView({ behavior: 'smooth' });
        }, 350);
    }, [product, isPartnerProduct, activeImageUrl, activeImageIndex, sendToOverlayTool, onClose]);

    // Lock body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            scrollPositionRef.current = window.scrollY;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollPositionRef.current}px`;
            document.body.style.width = '100%';
            document.body.style.overflowY = 'scroll';
        } else {
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.body.style.overflowY = '';
            if (scrollPositionRef.current > 0) {
                window.scrollTo(0, scrollPositionRef.current);
            }
            scrollPositionRef.current = 0;
        }
        return () => {
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.body.style.overflowY = '';
        };
    }, [isOpen]);

    // Handle ESC key
    useEffect(() => {
        const handleEscKey = (e) => {
            if (e.key === 'Escape' && isOpen) onClose();
        };
        window.addEventListener('keydown', handleEscKey);
        return () => window.removeEventListener('keydown', handleEscKey);
    }, [isOpen, onClose]);

    // Set default selected variant based on product type
    useEffect(() => {
        if (product) {
            console.log('Setting default variant for product:', product.name);
            console.log('Product images (raw):', product.images);
            console.log('Product images (full URLs):', getFullImageUrls(product.images));

            if (isPartnerProduct) {
                const productOption = {
                    sku: 'product-default',
                    color: 'Producto',
                    images: getFullImageUrls(product.productImages || []),
                    isProductOption: true
                };
                console.log('Partner product option images:', productOption.images);
                setSelectedVariant(productOption);
            } else {
                const generalOption = {
                    sku: 'general-default',
                    color: 'Vista general',
                    images: getFullImageUrls(product.images || []),
                    isGeneralOption: true
                };
                console.log('Main product general option images:', generalOption.images);
                setSelectedVariant(generalOption);
            }
        }
    }, [product, isPartnerProduct]);

    // Memoize current images so the array reference only changes when selection actually changes
    const currentImages = useMemo(() => {
        if (!product) return [];

        // For partner products
        if (isPartnerProduct) {
            if (selectedVariant && selectedVariant.images?.length > 0) {
                return selectedVariant.images;
            }
            return getFullImageUrls(product.productImages || []);
        }

        // For main products - Vista general
        if (selectedVariant?.isGeneralOption) {
            return getFullImageUrls(product.images || []);
        }

        // For main products - specific color variant
        if (selectedVariant !== null && selectedVariant.images && selectedVariant.images.length > 0) {
            return selectedVariant.images;
        }

        return getFullImageUrls(product.images || []);
    }, [product, selectedVariant, isPartnerProduct]);

    // Format spec key
    const formatSpecKey = (key) => {
        return key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
    };

    // Render specification value with color support
    const renderSpecValue = (value) => {
        if (!value || value === '') return <span className="text-gray-400">-</span>;

        const stringValue = String(value);
        if (stringValue.includes('#')) {
            return <ColorDisplay colors={value} />;
        }

        if (Array.isArray(value)) {
            const hasColors = value.some(item => String(item).includes('#'));
            if (hasColors) {
                return <ColorDisplay colors={value} />;
            }
            return (
                <div className="space-y-1">
                    {value.map((item, idx) => (
                        <div key={idx} className="text-sm text-gray-700 break-words">
                            • {item}
                        </div>
                    ))}
                </div>
            );
        }

        if (typeof value === 'string' && value.includes(',')) {
            const items = value.split(',').map(item => item.trim());
            const hasColors = items.some(item => item.includes('#'));
            if (hasColors) {
                return <ColorDisplay colors={items} />;
            }
        }

        return <span className="text-sm text-gray-700 break-words">{value}</span>;
    };

    // Open full-size image
    const openFullSizeImage = useCallback((images, index) => {
        setFullSizeImage({
            isOpen: true,
            images,
            currentIndex: index
        });
    }, []);

    // Close full-size image
    const closeFullSizeImage = useCallback(() => {
        setFullSizeImage(prev => ({ ...prev, isOpen: false }));
    }, []);

    // Navigate full-size images
    const nextFullSizeImage = useCallback(() => {
        setFullSizeImage(prev => ({
            ...prev,
            currentIndex: (prev.currentIndex + 1) % prev.images.length
        }));
    }, []);

    const prevFullSizeImage = useCallback(() => {
        setFullSizeImage(prev => ({
            ...prev,
            currentIndex: (prev.currentIndex - 1 + prev.images.length) % prev.images.length
        }));
    }, []);

    const handleThumbnailClick = useCallback((index) => {
        setFullSizeImage(prev => ({
            ...prev,
            currentIndex: index
        }));
    }, []);

    if (!isOpen || !product) return null;

    return (
        <PortalModal isOpen={isOpen}>
            <AnimatePresence>
                {/* Backdrop */}
                <motion.div
                    key="modal-backdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-white/10 backdrop-blur-md glass-effect shadow-none z-[9999]"
                    onClick={onClose}
                />

                {/* Close button */}
                <motion.button
                    key="modal-close-btn"
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0 }}
                    onClick={onClose}
                    className="fixed top-4 right-4 z-[10000] bg-secondary/50 hover:bg-primary text-gray-700 hover:text-white p-3 rounded-full shadow-2xl transition-all border border-gray-200"
                >
                    <X size={24} />
                </motion.button>

                {/* Modal content */}
                <motion.div
                    key="modal-content"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="fixed inset-4 md:inset-8 z-[9999] overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="min-h-full flex items-center justify-center py-8">
                        <div className="bg-white/80 rounded-xl max-w-6xl w-full overflow-hidden shadow-xl">
                            {/* Header */}
                            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 z-10">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="bg-primary text-white text-xs px-3 py-1 rounded-full">
                                        Categoría: {product.category}
                                    </span>
                                    {product.subCategory && (
                                        <span className="bg-gray-100 text-gray-700 text-xs px-3 py-1 rounded-full">
                                            Subcategoría: {product.subCategory}
                                        </span>
                                    )}
                                    {product.brand && (
                                        <span className="bg-gray-100 text-gray-700 text-xs px-3 py-1 rounded-full">
                                            Marca: {product.brand}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Content */}
                            <div className="p-6 max-h-[calc(100vh-180px)] overflow-y-auto">
                                <div className="grid md:grid-cols-2 gap-8">
                                    {/* Left column - Images */}
                                    <div>
                                        <ImageGallery
                                            key={`gallery-${selectedVariant?.sku || 'general'}`}
                                            images={currentImages}
                                            productName={product.name}
                                            onImageClick={openFullSizeImage}
                                            onActiveImageChange={handleActiveImageChange}
                                        />
                                    </div>

                                    {/* Right column - Details - Fixed text overflow */}
                                    <div className="space-y-6 overflow-hidden">
                                        <div>
                                            <div className="text-3xl font-bold text-primary mb-4 break-words">
                                                {product.name}
                                            </div>
                                            <p className="text-gray-700 text-sm leading-relaxed break-words whitespace-normal">
                                                {product.longDesc || product.shortDesc}
                                            </p>
                                        </div>

                                        {/* Features */}
                                        {product.features && product.features.length > 0 && (
                                            <div className="bg-gray-50 p-4 rounded-lg">
                                                <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                                                    <Check size={20} className="mr-2 text-green-500 flex-shrink-0" />
                                                    Características:
                                                </h4>
                                                <ul className="space-y-2">
                                                    {product.features.map((feature, index) => (
                                                        <li key={index} className="text-sm text-gray-700 flex items-start">
                                                            <span className="text-primary mr-2 flex-shrink-0">•</span>
                                                            <span className="break-words">{feature}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {/* Variant selector for main products with colors */}
                                        {!isPartnerProduct && product.variants && product.variants.length > 0 && (
                                            <ColorVariantSelector
                                                variants={product.variants}
                                                selectedVariant={selectedVariant}
                                                onVariantSelect={setSelectedVariant}
                                                productImages={product.images}
                                            />
                                        )}

                                        {/* Variant selector for partner products */}
                                        {isPartnerProduct && product.variants && product.variants.length > 0 && (
                                            <PartnerVariantSelector
                                                variants={product.variants}
                                                selectedVariant={selectedVariant}
                                                onVariantSelect={setSelectedVariant}
                                                productImages={product.productImages}
                                            />
                                        )}

                                        {/* Action buttons */}
                                        <div className="pt-4 space-y-3">
                                            <button
                                                onClick={() => onQuote(product.name)}
                                                className="w-full bg-primary hover:bg-primary/80 text-white py-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                            >
                                                <QuoteIcon size={16} />
                                                Solicitar cotización
                                            </button>
                                            {/* Send selected image to OverlayTool canvas */}
                                            <button
                                                onClick={handleSendToOverlayTool}
                                                className="w-full bg-secondary hover:bg-secondary/80 py-3 rounded-lg text-sm font-bold transition-colors flex items-center justify-center gap-2 text-white"
                                            >
                                                <Layers size={16} />
                                                Visualizar con mi logo
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Technical specs grid - Fixed text overflow */}
                                {product.technicalSpecs && Object.keys(product.technicalSpecs).filter(key => product.technicalSpecs[key]).length > 0 && (
                                    <div className="mt-8 pt-6 border-t border-gray-200">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Especificaciones técnicas</h3>
                                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                            {Object.entries(product.technicalSpecs)
                                                .filter(([_, value]) => value && value !== '' && value !== null)
                                                .map(([key, value]) => (
                                                    <div key={key} className="bg-gray-50 p-3 rounded-lg overflow-hidden">
                                                        <div className="text-xs text-gray-500 font-medium mb-1 uppercase tracking-wider truncate" title={formatSpecKey(key)}>
                                                            {formatSpecKey(key)}
                                                        </div>
                                                        <div className="text-sm text-gray-900 break-words">
                                                            {renderSpecValue(value)}
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Full-size image modal */}
                <FullSizeImageModal
                    images={fullSizeImage.images}
                    currentIndex={fullSizeImage.currentIndex}
                    isOpen={fullSizeImage.isOpen}
                    onClose={closeFullSizeImage}
                    onNext={nextFullSizeImage}
                    onPrev={prevFullSizeImage}
                    onThumbnailClick={handleThumbnailClick}
                />
            </AnimatePresence>
        </PortalModal>
    );
};

export default ProductDetailModal;